import { useQuery } from "@tanstack/react-query";
import { api } from "../api";

/** One of the deterministic tier classifications from rules.md §15-23. */
export type InsightTier =
  | "Strong"
  | "Resilient"
  | "Counterfactual"
  | "Constructive";

/**
 * Per-box payload as the backend ships it.
 *
 * - `badge` is empty for the hero card; otherwise: "Pace", "Forecast",
 *   "Mix", "Variance", "Pick-Up".
 * - `body` contains Markdown-style `**bold**` callouts. The frontend parses
 *   these into `<b>` tags via the helper in ChartInsight's call sites.
 * - `opportunity` is the optional italic "Opportunity: ..." append used only
 *   on the Mix box when a parity edge is genuinely warranted (rules.md §63).
 */
export interface InsightBoxOutput {
  badge: string;
  body: string;
  opportunity?: string;
  tier: InsightTier;
}

export interface DashboardInsightsResponse {
  hero: InsightBoxOutput;
  pace: InsightBoxOutput;
  forecast: InsightBoxOutput;
  mix: InsightBoxOutput;
  variance: InsightBoxOutput;
  pickup: InsightBoxOutput;
  /** ISO timestamp when the backend computed this batch. */
  generatedAt: string;
}

/**
 * Fetch the 6 main-dashboard insights (1 hero + 5 chart pills) in one call.
 * Backend caches per-user for 24h (rules.md §13 "daily floor"), so this
 * hook's 5-min staleTime is just the FE-side dedupe window.
 */
export function useDashboardInsights(userId: string | undefined) {
  return useQuery<DashboardInsightsResponse>({
    queryKey: ["dashboard-insights", userId],
    queryFn: () =>
      api.dashboardInsights.get(userId as string) as Promise<
        DashboardInsightsResponse
      >,
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });
}
